import React, { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const QUESTIONS = [
  {
    id: "opening",
    prefix: "With God's help, I am a leader who",
    prompt: "Complete the sentence — who are you as a leader?",
    placeholder: "...inspires others to believe in themselves",
  },
  {
    id: "character",
    prefix: "One word that describes the kind of leader I want to be is",
    prompt: "What one word defines your leadership character?",
    placeholder: "...servant, courageous, faithful, wise...",
  },
  {
    id: "belief",
    prefix: "I believe that leadership is not about position but about",
    prompt: "What do you believe leadership is really about?",
    placeholder: "...influence, character, serving others...",
  },
  {
    id: "people",
    prefix: "The people I most want to lead and serve are",
    prompt: "Who are the people you feel called to lead?",
    placeholder: "...young people in my community, my classmates...",
  },
  {
    id: "impact",
    prefix: "In five years, because of how I lead, the people around me will",
    prompt: "What impact do you want to have on the people around you?",
    placeholder: "...be more confident, feel seen and valued...",
  },
];

export default function IdentityStatement() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    opening: "",
    character: "",
    belief: "",
    people: "",
    impact: "",
  });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewFull, setViewFull] = useState(false);

  useEffect(() => {
    async function fetchStatement() {
      try {
        const docRef = doc(db, "identityStatements", currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setAnswers(docSnap.data().answers);
          setSaved(true);
          setViewFull(true);
        }
      } catch (err) {
        console.error("Error fetching identity statement:", err);
      }
      setLoading(false);
    }
    fetchStatement();
  }, [currentUser]);

  function handleChange(e) {
    setAnswers({ ...answers, [QUESTIONS[step].id]: e.target.value });
  }

  function handleNext() {
    if (!answers[QUESTIONS[step].id].trim()) return;
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      setViewFull(true);
    }
  }

  function handleBack() {
    if (step > 0) setStep(step - 1);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await setDoc(doc(db, "identityStatements", currentUser.uid), {
        answers,
        updatedAt: new Date().toISOString(),
        uid: currentUser.uid,
      });
      setSaved(true);
    } catch (err) {
      console.error("Error saving identity statement:", err);
    }
    setSaving(false);
  }

  function handleEdit() {
    setViewFull(false);
    setStep(0);
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <button onClick={() => navigate("/scholar")} style={styles.backButton}>
          Back to Dashboard
        </button>
        <div style={styles.topBarTitle}>Identity Statement</div>
      </div>

      <div style={styles.content}>
        {!viewFull ? (
          <div style={styles.card}>
            <div style={styles.progressRow}>
              {QUESTIONS.map((q, i) => (
                <div
                  key={q.id}
                  style={{
                    ...styles.progressDot,
                    backgroundColor: i <= step ? "#C9A84C" : "#e0e0e0",
                  }}
                />
              ))}
            </div>

            <p style={styles.stepLabel}>
              Question {step + 1} of {QUESTIONS.length}
            </p>

            <h2 style={styles.prefix}>
              {QUESTIONS[step].prefix}...
            </h2>

            <p style={styles.prompt}>{QUESTIONS[step].prompt}</p>

            <textarea
              value={answers[QUESTIONS[step].id]}
              onChange={handleChange}
              style={styles.textarea}
              placeholder={QUESTIONS[step].placeholder}
              rows={4}
            />

            <div style={styles.buttonRow}>
              {step > 0 && (
                <button onClick={handleBack} style={styles.backBtn}>
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                style={{
                  ...styles.nextBtn,
                  opacity: answers[QUESTIONS[step].id].trim() ? 1 : 0.5,
                }}
                disabled={!answers[QUESTIONS[step].id].trim()}
              >
                {step === QUESTIONS.length - 1 ? "See My Statement" : "Next"}
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.card}>
            <div style={styles.statementHeader}>
              <h2 style={styles.statementTitle}>Your Identity Statement</h2>
              {saved && (
                <span style={styles.savedBadge}>Saved</span>
              )}
            </div>

            <div style={styles.statementBox}>
              <p style={styles.statementText}>
                With God's help, I am a leader who <strong>{answers.opening}</strong>.
                One word that describes the kind of leader I want to be is <strong>{answers.character}</strong>.
                I believe that leadership is not about position but about <strong>{answers.belief}</strong>.
                The people I most want to lead and serve are <strong>{answers.people}</strong>.
                In five years, because of how I lead, the people around me will <strong>{answers.impact}</strong>.
              </p>
            </div>

            <div style={styles.answersGrid}>
              {QUESTIONS.map((q) => (
                <div key={q.id} style={styles.answerCard}>
                  <p style={styles.answerPrefix}>{q.prefix}</p>
                  <p style={styles.answerText}>{answers[q.id]}</p>
                </div>
              ))}
            </div>

            <div style={styles.buttonRow}>
              <button onClick={handleEdit} style={styles.backBtn}>
                Edit
              </button>
              <button
                onClick={handleSave}
                style={styles.nextBtn}
                disabled={saving}
              >
                {saving ? "Saving..." : saved ? "Update Statement" : "Save Statement"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f5f6fa",
    fontFamily: "'Segoe UI', sans-serif",
  },
  topBar: {
    backgroundColor: "#0D1B2A",
    padding: "16px 40px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  backButton: {
    backgroundColor: "transparent",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "6px",
    color: "rgba(255,255,255,0.7)",
    fontSize: "13px",
    padding: "8px 16px",
    cursor: "pointer",
  },
  topBarTitle: {
    color: "#C9A84C",
    fontSize: "16px",
    fontWeight: "700",
    letterSpacing: "1px",
    textTransform: "uppercase",
  },
  content: {
    maxWidth: "680px",
    margin: "0 auto",
    padding: "48px 20px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "48px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  },
  progressRow: {
    display: "flex",
    gap: "8px",
    marginBottom: "24px",
  },
  progressDot: {
    width: "32px",
    height: "6px",
    borderRadius: "999px",
    transition: "background 0.3s",
  },
  stepLabel: {
    fontSize: "12px",
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: "1px",
    margin: "0 0 12px 0",
    fontWeight: "600",
  },
  prefix: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#0D1B2A",
    margin: "0 0 8px 0",
    lineHeight: "1.4",
  },
  prompt: {
    fontSize: "14px",
    color: "#888",
    margin: "0 0 24px 0",
  },
  textarea: {
    width: "100%",
    padding: "16px",
    borderRadius: "10px",
    border: "1.5px solid #e0e0e0",
    fontSize: "15px",
    color: "#0D1B2A",
    resize: "vertical",
    outline: "none",
    lineHeight: "1.6",
    boxSizing: "border-box",
    fontFamily: "'Segoe UI', sans-serif",
  },
  buttonRow: {
    display: "flex",
    gap: "12px",
    marginTop: "24px",
    justifyContent: "flex-end",
  },
  backBtn: {
    backgroundColor: "transparent",
    border: "1.5px solid #e0e0e0",
    borderRadius: "8px",
    padding: "12px 24px",
    fontSize: "14px",
    color: "#666",
    cursor: "pointer",
    fontWeight: "600",
  },
  nextBtn: {
    backgroundColor: "#C9A84C",
    border: "none",
    borderRadius: "8px",
    padding: "12px 32px",
    fontSize: "14px",
    color: "#0D1B2A",
    cursor: "pointer",
    fontWeight: "700",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  statementHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  statementTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#0D1B2A",
    margin: "0",
  },
  savedBadge: {
    backgroundColor: "#dcfce7",
    color: "#16a34a",
    fontSize: "11px",
    fontWeight: "700",
    padding: "4px 12px",
    borderRadius: "20px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statementBox: {
    backgroundColor: "#fef9ec",
    border: "1.5px solid #C9A84C",
    borderRadius: "12px",
    padding: "28px",
    marginBottom: "28px",
  },
  statementText: {
    fontSize: "16px",
    color: "#0D1B2A",
    lineHeight: "1.8",
    margin: "0",
  },
  answersGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "8px",
  },
  answerCard: {
    backgroundColor: "#f5f6fa",
    borderRadius: "8px",
    padding: "16px 20px",
  },
  answerPrefix: {
    fontSize: "11px",
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    margin: "0 0 4px 0",
    fontWeight: "600",
  },
  answerText: {
    fontSize: "14px",
    color: "#0D1B2A",
    margin: "0",
    fontWeight: "500",
  },
  loadingText: {
    color: "#999",
    fontSize: "14px",
    textAlign: "center",
    paddingTop: "100px",
  },
};